import httpService from "./httpService";

/**
 * Servicio de Boletos
 * Maneja operaciones relacionadas con diseños de invitaciones y generación de boletos con QR
 */
class BoletoService {
  /**
   * Subir diseño base de invitación (Admin)
   * @param {string} eventoId - ID del evento
   * @param {File} imageFile - Archivo de imagen (PNG/JPEG)
   * @returns {Promise<Object>}
   */
  async subirDiseno(eventoId, imageFile) {
    try {
      const formData = new FormData();
      formData.append("imagen", imageFile);

      const response = await httpService.post(
        `/eventos/${eventoId}/boletos/diseno`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          timeout: 120000, // 2 minutos para archivos grandes
        }
      );

      return {
        success: true,
        data: response.data || response,
        message: response.message || "Diseño subido exitosamente",
      };
    } catch (error) {
      return {
        success: false,
        error: error.userMessage || "Error al subir diseño",
        details: error,
      };
    }
  }

  /**
   * Guardar configuración de posición y tamaño del QR (Admin)
   * @param {string} eventoId - ID del evento
   * @param {Object} qrConfig - { x, y, width, height } en porcentajes (0-1)
   * @returns {Promise<Object>}
   */
  async guardarConfiguracionQR(eventoId, qrConfig) {
    try {
      const response = await httpService.put(
        `/eventos/${eventoId}/boletos/configuracion-qr`,
        { qr_config: qrConfig }
      );

      return {
        success: true,
        data: response.data || response,
        message: response.message || "Configuración guardada exitosamente",
      };
    } catch (error) {
      return {
        success: false,
        error: error.userMessage || "Error al guardar configuración",
        details: error,
      };
    }
  }

  /**
   * Obtener configuración actual del boleto de un evento
   * @param {string} eventoId - ID del evento
   * @returns {Promise<Object>}
   */
  async obtenerConfiguracion(eventoId) {
    try {
      const response = await httpService.get(
        `/eventos/${eventoId}/boletos/configuracion`
      );

      return {
        success: true,
        data: response.data || response,
      };
    } catch (error) {
      // 404 es esperado si no hay configuración
      if (error.status === 404) {
        return {
          success: false,
          notFound: true,
          error: "No hay configuración de boleto para este evento",
        };
      }

      return {
        success: false,
        error: error.userMessage || "Error al obtener configuración",
        details: error,
      };
    }
  }

  /**
   * Generar boleto individual con QR
   * @param {string} eventoId - ID del evento
   * @param {string} invitadoId - ID del invitado
   * @returns {Promise<Object>}
   */
  async generarBoletoIndividual(eventoId, invitadoId) {
    try {
      const response = await httpService.get(
        `/eventos/${eventoId}/boletos/invitado/${invitadoId}`
      );

      return {
        success: true,
        data: response.data || response,
        message: "Boleto generado exitosamente",
      };
    } catch (error) {
      return {
        success: false,
        error: error.userMessage || "Error al generar boleto",
        details: error,
      };
    }
  }

  /**
   * Listar boletos generados disponibles para descarga
   * @param {string} eventoId
   * @returns {Promise<Object>}
   */
  async listarBoletosDisponibles(eventoId) {
    try {
      const response = await httpService.get(
        `/eventos/${eventoId}/boletos/disponibles`
      );
      return { success: true, data: response?.data || response };
    } catch (error) {
      return {
        success: false,
        error: error.userMessage || "Error al listar boletos",
      };
    }
  }

  /**
   * Descargar boletos seleccionados en ZIP (Admin)
   * La API genera el ZIP en blob storage y devuelve una URL SAS para descarga directa.
   * Genera el boleto si no existe aún.
   * @param {string} eventoId - ID del evento
   * @param {string[]} invitadoIds - IDs de invitados seleccionados
   * @returns {Promise<Object>}
   */
  async descargarBoletosMasivo(eventoId, invitadoIds = []) {
    try {
      const response = await httpService.post(
        `/eventos/${eventoId}/boletos/descargar-masivo`,
        { invitadoIds },
        { timeout: 300000 }
      );

      // La API devuelve { url, total_boletos, expira_en }
      const downloadUrl = response?.data?.url || response?.url;
      if (!downloadUrl) throw new Error("No se recibió URL de descarga");

      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `boletos_evento_${eventoId}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      return {
        success: true,
        message: `Descarga iniciada: ${response?.data?.total_boletos ?? ""} boletos`,
      };
    } catch (error) {
      return {
        success: false,
        error: error.userMessage || "Error al descargar boletos",
        details: error,
      };
    }
  }

  /**
   * Eliminar todos los boletos generados de un evento
   * @param {string} eventoId
   * @returns {Promise<Object>}
   */
  async eliminarBoletos(eventoId) {
    try {
      const response = await httpService.delete(
        `/eventos/${eventoId}/boletos`
      );
      return { success: true, data: response?.data || response };
    } catch (error) {
      return {
        success: false,
        error: error.userMessage || "Error al eliminar boletos",
      };
    }
  }

  /**
   * Enviar boletos por correo usando los datos del sistema (sin Excel)
   * @param {string} eventoId
   * @param {string[]} invitadoIds
   * @returns {Promise<Object>}
   */
  async enviarBoletosSistema(eventoId, invitadoIds = []) {
    try {
      const response = await httpService.post(
        `/eventos/${eventoId}/boletos/enviar-sistema`,
        { invitadoIds },
        { timeout: 600000 } // 10 min: puede generar y enviar muchos boletos
      );
      return { success: true, data: response?.data || response };
    } catch (error) {
      return {
        success: false,
        error: error.userMessage || "Error al enviar boletos",
      };
    }
  }

  /**
   * Compartir boleto (Web Share API)
   * @param {string} boletoUrl - URL del boleto
   * @param {string} nombreInvitado - Nombre del invitado
   * @returns {Promise<Object>}
   */
  async compartirBoleto(boletoUrl, nombreInvitado = "graduado") {
    try {
      if (!navigator.share) {
        throw new Error("Compartir no disponible en este navegador");
      }

      // Descargar imagen como blob
      const response = await fetch(boletoUrl);
      const blob = await response.blob();
      const file = new File([blob], `boleto_${nombreInvitado}.png`, {
        type: "image/png",
      });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: "Mi Boleto de Invitación",
          text: `Boleto de invitación`,
          files: [file],
        });

        return {
          success: true,
          message: "Boleto compartido exitosamente",
        };
      } else {
        // Fallback: descargar
        const link = document.createElement("a");
        link.href = boletoUrl;
        link.download = `boleto_${nombreInvitado}.png`;
        link.click();

        return {
          success: true,
          message: "Boleto descargado (compartir no disponible)",
        };
      }
    } catch (error) {
      if (error.name === "AbortError") {
        // Usuario canceló el compartir
        return {
          success: false,
          cancelled: true,
          error: "Compartir cancelado",
        };
      }

      return {
        success: false,
        error: error.message || "Error al compartir boleto",
        details: error,
      };
    }
  }

  /**
   * Enviar boletos personalizados a partir de un archivo Excel
   * @param {string} eventoId - ID del evento
   * @param {File} excelFile - Archivo .xlsx / .xls
   * @param {Object} columnMapping - {
   *   columna_nombre, columna_correo, columna_boletos,
   *   columna_correo_secundario?, columnas_qr: string[],
   *   nombre_evento?
   * }
   * @returns {Promise<Object>}
   */
  async enviarBoletosExcel(eventoId, excelFile, columnMapping) {
    try {
      const formData = new FormData();
      formData.append("excel", excelFile);
      formData.append("config", JSON.stringify(columnMapping));

      const response = await httpService.post(
        `/eventos/${eventoId}/boletos/enviar-excel`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 300000, // 5 minutos para procesar muchas filas
        }
      );

      return {
        success: true,
        data: response.data || response,
        message: response.message || "Boletos enviados exitosamente",
      };
    } catch (error) {
      return {
        success: false,
        error: error.userMessage || "Error al enviar boletos por Excel",
        details: error,
      };
    }
  }

  /**
   * Validar archivo de imagen antes de subir
   * @param {File} file - Archivo a validar
   * @returns {Object} { valid: boolean, error?: string }
   */
  validarImagen(file) {
    // Validar tipo
    const tiposPermitidos = ["image/png", "image/jpeg", "image/jpg"];
    if (!tiposPermitidos.includes(file.type)) {
      return {
        valid: false,
        error: "Solo se aceptan imágenes PNG o JPEG",
      };
    }

    // Validar tamaño (100 MB)
    const maxSize = 100 * 1024 * 1024; // 100 MB
    if (file.size > maxSize) {
      return {
        valid: false,
        error: "El archivo excede el tamaño máximo de 100 MB",
      };
    }

    return { valid: true };
  }
}

// Exportar instancia singleton
const boletoService = new BoletoService();
export default boletoService;
